// サイドメニューの定義と、表示する項目の決め方。
// デスクトップのサイドメニュー・スマホのメニュー・下部タブ・検索・サーバ側の検証がすべてこれを使う
// （CLAUDE.md §4：ナビは全実装で同一）。
import type { Role } from "./types";
import type { Capability } from "./permissions";

export type NavIconName =
  | "home" | "chart" | "chat" | "document" | "briefcase" | "users" | "building"
  | "store" | "cash" | "check" | "calendar" | "adjust" | "clipboard" | "refund"
  | "shield" | "usercog" | "cog" | "upload" | "bell" | "grid" | "org";

export interface NavItem {
  to: string;
  label: string;
  icon: NavIconName;
  cap?: Capability;
  roles?: Role[]; // 指定時はこのロールを持つユーザーのみ表示
  external?: boolean; // 外部URL（別タブで開く）
}
export interface NavGroup {
  id: string;     // 並び替え・折りたたみの保存に使う（表示名が変わっても崩れない）
  label?: string; // 見出し。無いグループ（先頭）は折りたためない
  items: NavItem[];
}

// 組織（全員）のカスタマイズ：管理者が設定（非表示/名称/並び順）
export type NavCustom = Record<string, { hidden: boolean; label: string | null; sort_order: number | null }>;

// 業務の流れ順にグループ化（営業 → 案件・紹介 → 取引先 → 入出金・承認 → 管理）
export const NAV_GROUPS: NavGroup[] = [
  { id: "top", items: [
    { to: "/", label: "ダッシュボード", icon: "home" },
    { to: "/exec", label: "経営ダッシュボード", icon: "chart", roles: ["owner", "finance"] },
    { to: "/locoreach-analytics", label: "LOCOREACH分析", icon: "chart", cap: "finance.read" },
    { to: "/operations", label: "総務業務課", icon: "grid", roles: ["owner", "admin", "finance", "sales", "viewer"] },
    { to: "https://meoreport.camomile.co.jp/", label: "MEOレポート", icon: "chart", external: true },
  ] },
  { id: "sales", label: "営業", items: [
    { to: "/follow-ups", label: "連絡フォロー", icon: "bell", cap: "task.manage" },
    { to: "/sales", label: "営業ダッシュボード", icon: "chart", cap: "sales_mgmt.read" },
    { to: "/opportunities", label: "商談", icon: "chat", cap: "sales_mgmt.read" },
    { to: "/collab", label: "協業案件", icon: "chat", cap: "sales_mgmt.read" },
    { to: "/sns-leads", label: "SNS案件", icon: "users", cap: "sales_mgmt.read" },
    { to: "/sales-reports", label: "営業報告", icon: "document", cap: "sales_mgmt.read" },
    { to: "/expenses", label: "経費精算", icon: "cash", roles: ["owner", "finance", "sales"] },
  ] },
  { id: "deals", label: "案件・紹介", items: [
    { to: "/deals", label: "案件", icon: "briefcase" },
    { to: "/subsidy", label: "補助金申請", icon: "clipboard", cap: "subsidy_case.read" },
    { to: "/contracts", label: "電子契約", icon: "document", cap: "deal.read.all" },
    { to: "/d-leads", label: "紹介先", icon: "chat", cap: "lead.read" },
  ] },
  { id: "partners", label: "取引先", items: [
    { to: "/companies", label: "法人・事業者", icon: "org", cap: "customer.read" },
    { to: "/customers", label: "顧客（店舗）", icon: "building", cap: "customer.read" },
    { to: "/partners", label: "紹介者", icon: "users", cap: "partner.read" },
    { to: "/vendors", label: "提案企業", icon: "store", cap: "vendor.read" },
  ] },
  { id: "money", label: "入出金・承認", items: [
    { to: "/store-payments", label: "決済状況", icon: "cash", cap: "deal.read.all" },
    { to: "/payments", label: "請求書支払", icon: "cash", cap: "invoice.manage" },
    { to: "/payroll", label: "給与計算", icon: "cash", cap: "payroll.manage" },
    { to: "/my-payslip", label: "支払通知書", icon: "cash", cap: "payroll.self" },
    { to: "/approvals", label: "承認待ち", icon: "check", cap: "approval.view" },
    { to: "/refunds", label: "返金・相殺", icon: "refund", cap: "refund.manage" },
    { to: "/closings", label: "月次締め", icon: "calendar", cap: "closing.manage" },
    { to: "/rules", label: "報酬ルール", icon: "adjust", cap: "commission_rule.manage" },
  ] },
  { id: "notify", label: "通知", items: [
    { to: "/notification-settings", label: "通知設定", icon: "bell" },
    { to: "/notifications/send", label: "通知を送る", icon: "chat", roles: ["owner", "admin"] },
  ] },
  { id: "admin", label: "管理", items: [
    { to: "/storage", label: "資料格納", icon: "grid", cap: "document.read" },
    { to: "/domains", label: "ドメイン台帳", icon: "shield", cap: "domain.read" },
    { to: "/interviewees", label: "面接者", icon: "usercog", cap: "interviewee.read" },
    { to: "/tasks", label: "タスク", icon: "clipboard", cap: "task.manage" },
    { to: "/import", label: "データ取込", icon: "upload", cap: "customer.manage" },
    { to: "/audit", label: "監査ログ", icon: "shield", cap: "audit.read" },
    { to: "/trash", label: "ゴミ箱", icon: "refund", cap: "audit.read" },
    { to: "/users", label: "ユーザー", icon: "usercog", cap: "users.manage" },
    { to: "/settings", label: "設定", icon: "cog", cap: "settings.manage" },
  ] },
];

// スマホ下部タブの優先順（権限のあるものを先頭から最大4つ＋メニュー）
export const MOBILE_PRIORITY = ["/", "/opportunities", "/deals", "/d-leads", "/customers", "/expenses", "/payroll"];
// 税理士アカウントが見られる画面（給与計算＝支払通知書のみ）
export const TAX_ALLOW = ["/payroll"];

export const NAV_KEYS: ReadonlySet<string> = new Set(NAV_GROUPS.flatMap((g) => g.items.map((i) => i.to)));
export const NAV_GROUP_IDS: ReadonlySet<string> = new Set(NAV_GROUPS.map((g) => g.id));
const FOLDABLE: ReadonlySet<string> = new Set(NAV_GROUPS.filter((g) => g.label).map((g) => g.id));

export interface NavViewer {
  hasCap: (cap: Capability) => boolean;
  hasRole: (...roles: Role[]) => boolean;
}

/** 権限で見られる項目に、組織のカスタマイズ（非表示・名称・並び順）を反映したメニュー。 */
export function permittedNav(viewer: NavViewer, org: NavCustom = {}): NavGroup[] {
  const taxOnly = viewer.hasRole("tax");
  return NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items
        .filter((i) => !taxOnly || TAX_ALLOW.includes(i.to))
        .filter((i) => (!i.cap || viewer.hasCap(i.cap)) && (!i.roles || i.roles.some((r) => viewer.hasRole(r))))
        .filter((i) => !org[i.to]?.hidden)
        .map((i) => ({ ...i, label: org[i.to]?.label || i.label }))
        .map((i, idx) => ({ i, ord: org[i.to]?.sort_order ?? idx }))
        .sort((a, b) => a.ord - b.ord)
        .map((x) => x.i),
    }))
    .filter((g) => g.items.length > 0);
}

// ---------------- 自分の画面（ユーザーごと）の表示・色 ----------------
export interface UserNavPrefs {
  accent?: string | null; // サイドメニュー全体の色（選択中の項目）
  items?: Record<string, { hidden?: boolean; color?: string | null }>; // 項目ごとの表示・アイコン色
  layout?: Record<string, string[]>; // 並び替え：グループID → 項目（リンク先）の並び。別のグループへ移した項目もここに入る
  folded?: string[];                 // 折りたたんだグループID
}

export const DEFAULT_ACCENT = "#16a34a"; // brand-600
// LocoWorks の「表示・テーマ」と同じ並び（緑・青・藍・紫・青緑・赤・橙・灰）
export const NAV_ACCENTS: { name: string; hex: string }[] = [
  { name: "緑", hex: "#16a34a" }, { name: "青", hex: "#2563eb" }, { name: "藍", hex: "#4f46e5" }, { name: "紫", hex: "#9333ea" },
  { name: "青緑", hex: "#0d9488" }, { name: "赤", hex: "#dc2626" }, { name: "橙", hex: "#ea580c" }, { name: "灰", hex: "#475569" },
];

const HEX = /^#[0-9a-f]{6}$/;
const hexOf = (v: unknown): string | null => (typeof v === "string" && HEX.test(v.trim().toLowerCase()) ? v.trim().toLowerCase() : null);

/** 保存値を検証して整える。未知の項目・不正な色は捨てる（既定のままの項目は保存しない）。 */
export function normalizeNavPrefs(raw: unknown): UserNavPrefs {
  const out: UserNavPrefs = {};
  if (!raw || typeof raw !== "object") return out;
  const r = raw as { accent?: unknown; items?: unknown; layout?: unknown };
  const accent = hexOf(r.accent);
  if (accent && accent !== DEFAULT_ACCENT) out.accent = accent;
  if (r.items && typeof r.items === "object") {
    const items: NonNullable<UserNavPrefs["items"]> = {};
    for (const [k, v] of Object.entries(r.items as Record<string, unknown>)) {
      if (!NAV_KEYS.has(k) || !v || typeof v !== "object") continue;
      const hidden = (v as { hidden?: unknown }).hidden === true;
      const color = hexOf((v as { color?: unknown }).color);
      if (!hidden && !color) continue;
      items[k] = { ...(hidden ? { hidden: true } : {}), ...(color ? { color } : {}) };
    }
    if (Object.keys(items).length) out.items = items;
  }
  if (r.layout && typeof r.layout === "object") {
    const layout: Record<string, string[]> = {};
    const seen = new Set<string>();
    for (const [gid, list] of Object.entries(r.layout as Record<string, unknown>)) {
      if (!NAV_GROUP_IDS.has(gid) || !Array.isArray(list)) continue;
      const keys: string[] = [];
      for (const k of list) {
        if (typeof k !== "string" || !NAV_KEYS.has(k) || seen.has(k)) continue;
        seen.add(k); keys.push(k);
      }
      if (keys.length) layout[gid] = keys;
    }
    if (Object.keys(layout).length) out.layout = layout;
  }
  const folded = (r as { folded?: unknown }).folded;
  if (Array.isArray(folded)) {
    const f = [...new Set(folded.filter((g): g is string => typeof g === "string" && FOLDABLE.has(g)))];
    if (f.length) out.folded = f;
  }
  return out;
}

/**
 * 自分の並び（layout）を反映する。非表示の項目も含めて並べる（表示設定の一覧で使う）。
 * layout に無い項目（新しく増えた画面など）は、元のグループの最後に元の順で付く。
 * layout が指すグループがこの人に無い場合は、元のグループに戻す。
 */
export function arrangeNav(groups: NavGroup[], prefs: UserNavPrefs): NavGroup[] {
  const byKey = new Map(groups.flatMap((g) => g.items.map((i) => [i.to, i] as const)));
  const present = new Set(groups.map((g) => g.id));
  const placedIn = new Map<string, string>();
  for (const [gid, keys] of Object.entries(prefs.layout ?? {})) {
    if (!present.has(gid)) continue;
    for (const k of keys) if (byKey.has(k) && !placedIn.has(k)) placedIn.set(k, gid);
  }
  return groups.map((g) => {
    const ordered = (prefs.layout?.[g.id] ?? []).filter((k) => placedIn.get(k) === g.id).map((k) => byKey.get(k)!);
    const rest = g.items.filter((i) => !placedIn.has(i.to));
    return { ...g, items: [...ordered, ...rest] };
  });
}

/** 自分の並びを反映し、自分で非表示にした項目を除く（権限・組織の設定は permittedNav で反映済みのもの）。 */
export function applyUserPrefs(groups: NavGroup[], prefs: UserNavPrefs): NavGroup[] {
  return arrangeNav(groups, prefs)
    .map((g) => ({ ...g, items: g.items.filter((i) => !prefs.items?.[i.to]?.hidden) }))
    .filter((g) => g.items.length > 0);
}

/**
 * 項目 key を、グループ toGroup の beforeKey の前（null なら最後）へ移した並びを返す。
 * arranged は arrangeNav の結果（非表示の項目も含む）。非表示の項目の位置は保たれる。
 */
export function moveNavItem(arranged: NavGroup[], key: string, toGroup: string, beforeKey: string | null): Record<string, string[]> {
  const layout: Record<string, string[]> = {};
  for (const g of arranged) layout[g.id] = g.items.map((i) => i.to).filter((k) => k !== key);
  if (!layout[toGroup]) return Object.fromEntries(arranged.map((g) => [g.id, g.items.map((i) => i.to)]));
  const list = layout[toGroup];
  const at = beforeKey ? list.indexOf(beforeKey) : -1;
  list.splice(at < 0 ? list.length : at, 0, key);
  return layout;
}

function luminance(hex: string): number {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}
/** 色の上に載せる文字色（白か濃い灰の、読みやすい方）。 */
export function readableOn(hex: string): "#ffffff" | "#111827" {
  return contrastRatio(hex, "#ffffff") >= contrastRatio(hex, "#111827") ? "#ffffff" : "#111827";
}
/** 白い背景に文字として使える色か（コントラスト3以上）。足りなければ濃い灰を返す。 */
export function textColorOnWhite(hex: string): string {
  return contrastRatio(hex, "#ffffff") >= 3 ? hex : "#374151";
}
